import { UIEvent, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  App,
  Avatar,
  Button,
  Checkbox,
  Flex,
  Space,
  Spin,
  Typography,
} from "antd";
import {
  CheckOutlined,
  DeleteOutlined,
  UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  commonApi,
  NotificationInterface,
  NotificationsTypes,
  useBulkDeleteNotificationsMutation,
  useGetNotificationsQuery,
  useGetUnreadNotificationsQuery,
  useMarkAllReadNotificationsMutation,
  useSetReadNotificationMutation,
} from "api";
import { dateTimeFormat, format, getRouteByName } from "libs";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  resetAllNotificationsPage,
  resetUnreadNotificationsPage,
  setAllNotificationsPage,
  setUnreadNotificationsPage,
} from "store/appSettingsSlice";

interface NotificationsInterface {
  unread?: boolean;
  width?: number | string;
  height?: number | string;
}

// The backend caps each bulk-delete request at 50 ids, so larger
// selections are split into sequential batches of this size.
const BULK_DELETE_BATCH_SIZE = 50;

export const Notifications = ({
  unread = false,
  height = 450,
  width = 350,
}: NotificationsInterface) => {
  const navigate = useNavigate();
  const { modal } = App.useApp();

  const { allPage, unreadPage } = useAppSelector(
    (state) => state.appSettings.notifications
  );
  const dispatch = useAppDispatch();

  const { data, isLoading, isFetching } = (
    unread ? useGetUnreadNotificationsQuery : useGetNotificationsQuery
  )({
    page: unread ? unreadPage : allPage,
  });

  const [setNotificationReadMutation] = useSetReadNotificationMutation();
  const [markAllRead, { isLoading: isMarkingAll }] =
    useMarkAllReadNotificationsMutation();
  const [bulkDelete, { isLoading: isDeleting }] =
    useBulkDeleteNotificationsMutation();

  // Messenger-like selection: when active each row shows a checkbox and
  // tapping a row toggles it instead of navigating.
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const items = data?.data ?? [];

  // Reset the infinite-scroll page back to 1 after a bulk action so the
  // RTK `merge` doesn't keep stale appended pages on top of the
  // refetched page 1 (invalidatesTags refetches page 1).
  const resetPage = useCallback(() => {
    dispatch(
      unread ? resetUnreadNotificationsPage() : resetAllNotificationsPage()
    );
  }, [dispatch, unread]);

  const onScroll = (e: UIEvent<HTMLDivElement>) => {
    const { target }: { target: any } = e;

    if (
      target.scrollTop + target.offsetHeight === target.scrollHeight &&
      !isFetching &&
      (data?.pagination.currentPage || 1) < (data?.pagination.lastPage || 1)
    ) {
      dispatch(
        unread ? setUnreadNotificationsPage() : setAllNotificationsPage()
      );
    }
  };

  const getRightNavigationPath = (notification: NotificationInterface) => {
    switch (notification.type) {
      case NotificationsTypes.NEW_REPORT_CREATED:
        return {
          pathname: getRouteByName("ReportsPage"),
          search: `filters[id][$eq]=${notification.data.reportId}`,
        };
      case NotificationsTypes.NEW_POST_CREATED:
      case NotificationsTypes.UPCOMING_POST:
        return {
          pathname: getRouteByName("ListingPage", {
            listingId: notification.data.post,
          }),
        };
      case NotificationsTypes.NEW_USER_REGISTERED:
      case NotificationsTypes.USER_UPGRADE:
        return {
          pathname: getRouteByName("UserPage", {
            userId: notification.data.userId,
          }),
        };
      default:
        return { pathname: "#" };
    }
  };

  const onNotificationClick = useCallback(
    (notification: NotificationInterface) => {
      setNotificationReadMutation({ id: notification.id });

      dispatch(
        commonApi.util.updateQueryData(
          "getUnreadNotifications",
          { page: unreadPage },
          (data) => {
            data.data = data.data.filter((i) => i.id !== notification.id);
          }
        )
      );
      dispatch(
        commonApi.util.updateQueryData(
          "getNotifications",
          { page: allPage },
          (data) => {
            data.data = data.data.map((i) =>
              i.id === notification.id
                ? { ...i, readAt: dayjs().toString() }
                : i
            );
          }
        )
      );

      navigate(getRightNavigationPath(notification));
    },
    []
  );

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const allSelected = items.length > 0 && selectedIds.length === items.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const onToggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : items.map((i) => i.id));
  };

  const onToggleSelectionMode = () => {
    setSelectionMode((prev) => !prev);
    setSelectedIds([]);
  };

  const handleMarkAllRead = async () => {
    await markAllRead();
    resetPage();
    setSelectedIds([]);
    setSelectionMode(false);
  };

  // Delete the ids in sequential batches of 50 — the backend rejects
  // larger payloads, so we never send more than one batch at a time.
  const deleteInBatches = async (ids: string[]) => {
    for (let i = 0; i < ids.length; i += BULK_DELETE_BATCH_SIZE) {
      await bulkDelete({
        ids: ids.slice(i, i + BULK_DELETE_BATCH_SIZE),
      }).unwrap();
    }
  };

  const handleBulkDelete = () => {
    if (!selectedIds.length) return;

    const ids = [...selectedIds];

    modal.confirm({
      title: `Stergi ${ids.length} notificari?`,
      okText: "Sterge",
      okButtonProps: { danger: true },
      cancelText: "Anuleaza",
      onOk: async () => {
        await deleteInBatches(ids);
        resetPage();
        setSelectedIds([]);
        setSelectionMode(false);
      },
    });
  };

  return (
    <Spin spinning={isLoading || isFetching || isMarkingAll || isDeleting}>
      <Flex
        align="center"
        justify="space-between"
        gap={4}
        wrap="wrap"
        style={{ marginBottom: 8 }}
      >
        <Space size={4} wrap>
          <Button
            type="text"
            size="small"
            icon={<CheckOutlined />}
            onClick={handleMarkAllRead}
          >
            Marcheaza toate ca citite
          </Button>
          <Button type="text" size="small" onClick={onToggleSelectionMode}>
            {selectionMode ? "Renunta" : "Selecteaza"}
          </Button>
        </Space>

        {selectionMode && (
          <Space size={4} wrap>
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={onToggleSelectAll}
            >
              Toate
            </Checkbox>
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={!selectedIds.length}
              onClick={handleBulkDelete}
            >
              Sterge selectate ({selectedIds.length})
            </Button>
          </Space>
        )}
      </Flex>

      <div
        style={{ minHeight: 200, overflowY: "auto", height, width }}
        onScroll={onScroll}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          {items.map((i) => (
            <div
              key={i.id}
              style={{
                cursor: "pointer",
                borderRadius: 8,
                padding: 8,
              }}
              className={`${i.readAt ? "" : "not-readed"} clickable`}
              onClick={() =>
                selectionMode ? toggleSelect(i.id) : onNotificationClick(i)
              }
            >
              <Space>
                {selectionMode && (
                  <Checkbox
                    checked={selectedIds.includes(i.id)}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleSelect(i.id)}
                  />
                )}
                <Avatar src="" icon={<UserOutlined />} />
                <div>
                  <div>
                    <Typography.Text strong>{i.title}</Typography.Text>
                  </div>
                  <div>{format(i.createdAt, dateTimeFormat)}</div>
                </div>
              </Space>
            </div>
          ))}
        </Space>
      </div>
    </Spin>
  );
};
